import FastAverageColor from 'fast-average-color';
import classnames from 'classnames';
import { find, reject, omit, filter } from 'lodash';
import { __ } from '@wordpress/i18n';
import {
	useBlockProps,
	InnerBlocks,
	useInnerBlocksProps,
	__experimentalUseInnerBlocksProps,
	BlockControls,
	__experimentalBlockAlignmentMatrixToolbar as BlockAlignmentMatrixToolbar,
	__experimentalBlockAlignmentMatrixControl as BlockAlignmentMatrixControl,
} from '@wordpress/block-editor';
import {
	useDispatch,
	withSelect,
	useSelect,
	withDispatch,
} from '@wordpress/data';
import {
	compose,
} from '@wordpress/compose';
import {
	useEffect,
} from '@wordpress/element';
import {
	createBlock
} from '@wordpress/blocks';

import SlideInspectorControls from './inspector-controls';
import BackgroundDiv from '../../../components/reusable/background/BackgroundDiv';
import { getPositionClassName, getEmbedClassNames } from '../../utils';

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/developers/block-api/block-edit-save/#edit
 *
 * @param {Object} [props]           Properties passed from the editor.
 * @param {string} [props.className] Class name generated for the block.
 *
 * @return {WPElement} Element to render.
 */
function Edit( props ) {
	const {
		clientId,
		index,
		setAttributes,
		getEmbedPreview,
		isRequestingEmbedPreview,
		rootClientId,
		isBlockSelected,
		rootHasSelectedInnerBlock,
		replaceInnerBlocks,
		innerBlocks,
		attributes,
		updateBlockAttributes,
		removeBlock,
	} = props;

	const {
		background,
		contentPosition,
		minWidth,
		autoCaption,
	} = attributes;

	const {
		backgroundVideo,
		backgroundType,
	} = background;

	useEffect( () => {
		updateOrCreateAutoCaptionBlock();
	}, [ autoCaption ] );

	// useEffect( () => {
	// 	if ( elementRef.current ) {
	// 		retrieveFastAverageColor().getColorAsync(
	// 			elementRef.current
	// 		).then( ( color ) => {
	// 			setAttributes( {
	// 				background: {
	// 					...background,
	// 					backgroundColor: color.hex,
	// 				}
	// 			} )
	// 		});
	// 	}
	// }, [ elementRef.current ] );

	const hasNoMediaBackground = () => backgroundType !== 'image' &&
		backgroundType !== 'video';

	const hasEmbedBackground = () => backgroundType === 'video' &&
		backgroundVideo &&
		backgroundVideo.url &&
		backgroundVideo.embed &&
		backgroundVideo.embedData;

	const blockProps = useBlockProps( {
		className: classnames( {
			'swiper-slide': true,
			'no-media-background': hasNoMediaBackground(),
			'embed-background': hasEmbedBackground(),
			'ed-bg-video': backgroundType === 'video',
			'ed-bg-image': backgroundType === 'image',
			'ed-bg-none': backgroundType === 'none',
			'ed-bg-gradient': backgroundType === 'gradient',
			'ed-bg-color': backgroundType === 'color',
			},
			getPositionClassName( contentPosition )
		),
	});

	if ( [ 'none', 'gradient', 'color' ].indexOf( backgroundType ) !== -1 ) {
		blockProps.style = {
			'--slide-min-width': minWidth,
		}
	}

	let _useInnerBlocksProps;
	if ( typeof useInnerBlocksProps === 'function' ) {
		_useInnerBlocksProps = useInnerBlocksProps;
	} else {
		_useInnerBlocksProps = __experimentalUseInnerBlocksProps;
	}

	const innerBlocksProps = _useInnerBlocksProps( {}, {
		template: [
			[
				'core/paragraph',
				{
					placeholder: __( 'Add slide content here…', 'gutenslider' ),
					align: 'center',
					fontSize: 'larger',
					className: 'gutenslider-content-initial',
				},
			],
		],
	} );
	const { selectBlock } = useDispatch( 'core/block-editor' );

	function retrieveFastAverageColor() {
		if ( ! retrieveFastAverageColor.fastAverageColor ) {
			retrieveFastAverageColor.fastAverageColor = new FastAverageColor();
		}

		return retrieveFastAverageColor.fastAverageColor;
	}

	function onSelectSlide( e ) {
		if ( e.target.classList.contains( 'slide-content' ) ) {
			if ( ! isBlockSelected( rootClientId ) && ! rootHasSelectedInnerBlock ) {
				selectBlock( rootClientId );
			} else {
				selectBlock( clientId );
			}
		}
	}

	function updateOrCreateAutoCaptionBlock() {

		const { background } = attributes;
		let autoCaptionBlock = getAutoCaption();

		let theCaption = '';

		if ( background.backgroundType === 'image' &&
		typeof background.backgroundImage === 'object' &&
		background.backgroundImage !== null ) {
			if ( 'caption-only' === attributes.autoCaption ) {
				theCaption = background.backgroundImage.caption;
			} else if ( 'title-caption' === attributes.autoCaption ) {
				theCaption = `${ background.backgroundImage.title } - ${ background.backgroundImage.caption }`;
			} else if ( 'title-caption-description' === attributes.autoCaption ) {
				theCaption = `${ background.backgroundImage.title } - ${ background.backgroundImage.caption } - ${ attributes.background.backgroundImage.description }`;
			}
		}

		const autoCaptionAttributes = {
			content: theCaption,
		};

		if ( autoCaptionBlock !== null ) {
			if ( theCaption !== '' ) {
				return updateBlockAttributes( autoCaptionBlock.clientId, autoCaptionAttributes );
			}

			return removeBlock( autoCaptionBlock.clientId, false );
		}


		// or create the block if it does not exist and there is caption
		if ( theCaption !== '' ) {
			autoCaptionAttributes.className = 'eedee-gutenslider-autocaption';
			autoCaptionBlock = createBlock( 'core/paragraph', autoCaptionAttributes );

			const emptyInitialBlock = find( innerBlocks, {
				name: 'core/paragraph',
				attributes: {
					content: '',
					className: 'gutenslider-content-initial',
				},
			} );

			const newInnerBlocks =
				emptyInitialBlock ?
				[
					...reject(innerBlocks, emptyInitialBlock),
					autoCaptionBlock,
				] :
				[
					...innerBlocks,
					autoCaptionBlock,
				]

			// const newInnerBlocks = [ ...innerBlocks, autoCaptionBlock ];
			replaceInnerBlocks(
				clientId,
				newInnerBlocks,
				false
			);
		}
	}

	function getAutoCaption() {
		const { innerBlocks } = props;

		for ( const block of innerBlocks ) {
			const { className } = block.attributes;
			if ( block.name === 'core/paragraph' && className && className.includes( 'eedee-gutenslider-autocaption' ) ) {
				return block;
			}
		}
		return null;
	}

	return (
		<>
			<BlockControls>
				{ BlockAlignmentMatrixToolbar && <BlockAlignmentMatrixToolbar
					label={ __( 'Change content position', 'gutenslider' ) }
					value={ contentPosition }
					onChange={ ( nextPosition ) =>
						setAttributes( {
							contentPosition: nextPosition,
						} )
					}
				/> }
				{ BlockAlignmentMatrixControl && <BlockAlignmentMatrixControl
					label={ __( 'Change content position', 'gutenslider' ) }
					value={ contentPosition }
					onChange={ ( nextPosition ) =>
						setAttributes( {
							contentPosition: nextPosition,
						} )
					}
				/> }
			</BlockControls>
			<SlideInspectorControls
				attributes={ attributes } setAttributes={ setAttributes }
			/>
			<div onClick={ onSelectSlide } { ...blockProps }>
				<BackgroundDiv
					background={ background }
				/>
				<div className="slide-content">
					<div { ...innerBlocksProps }></div>
				</div>
			</div>
		</>
	);
}

export default compose( [
	withSelect( ( select, { clientId } ) => {
		const {
			getBlockIndex,
			getBlockParents,
			getBlockRootClientId,
			isBlockSelected,
			hasSelectedInnerBlock,
			getBlocks,
		} = select( 'core/block-editor' );

		const index = getBlockIndex( clientId, getBlockParents( clientId ) );
		const rootClientId = getBlockRootClientId( clientId );

		return {
			index,
			rootClientId,
			getBlockParents,
			isBlockSelected,
			innerBlocks: getBlocks( clientId ),
			rootHasSelectedInnerBlock: hasSelectedInnerBlock( rootClientId, false ),
		};
	} ),
	withDispatch( ( dispatch ) => {
		const {
			replaceInnerBlocks,
			updateBlockAttributes,
			removeBlock,
		} = dispatch( 'core/block-editor' );

		return {
			replaceInnerBlocks,
			updateBlockAttributes,
			removeBlock,
		};
	} ),
 ])( Edit )


